import { useQuery } from '@tanstack/react-query';

import { useOrpc } from '@/_shared/hooks/useOrpc';

/**
 * The counts for one account, read only while `accountId` is set (the dialog is open). Read on every
 * open: a cached count from an earlier open could miss what was added since. The dialog uses it too,
 * to hold its confirm until the counts are shown (the same query key, so one request).
 */
export function useRemovalImpact(accountId: string | null) {
  const orpc = useOrpc();
  return useQuery({
    ...orpc.settings.warehouses.removalImpact.queryOptions({ input: { accountId: accountId ?? '' } }),
    enabled: accountId !== null,
    staleTime: 0,
    // Closing the dialog drops the counts, so a reopen starts pending: no stale count, no early confirm.
    gcTime: 0,
    refetchOnMount: 'always',
  });
}

/** Confirm's tooltip while it waits for the counts. */
export const COUNTING_IMPACT = 'Counting what uses this account…';

/** Confirm waits for the counts; a failed read does not block it (they are advisory). */
export function removalImpactLoading(query: ReturnType<typeof useRemovalImpact>): boolean {
  // Not yet read since this dialog opened: cached counts from an earlier open do not count. `paused`
  // is the same wait offline: the request is queued, and confirming then would act before the counts.
  return query.fetchStatus !== 'idle' && !query.isFetchedAfterMount;
}
